import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { DocumentsAndJobsResponse } from '../../../../contracts';
import {
  COLLECTIONS_QUERY_KEY,
  COLLECTION_DETAIL_QUERY_KEY,
  DOCUMENTS_AND_JOBS_KEY,
  GET_USER_DOCUMENTS,
} from '../../../../contracts';
import { useTrackChatError } from '../../../../data/injected';
import type { OptimisticDeleteContext } from '../../../../data/library/optimistic-delete';
import {
  optimisticRemove,
  rollbackOptimisticRemove,
} from '../../../../data/library/optimistic-delete';
import { deleteUserDocument } from '../../../../data/library/user-documents/api';

/**
 * Mutation hook to delete a user document (with optimistic removal)
 */
export function useDeleteUserDocument() {
  const queryClient = useQueryClient();
  const { t } = useTranslations();
  const trackChatError = useTrackChatError();

  return useMutation<
    void,
    Error,
    string,
    OptimisticDeleteContext<DocumentsAndJobsResponse>
  >({
    mutationFn: deleteUserDocument,
    onMutate: async (documentId) => {
      await queryClient.cancelQueries({ queryKey: [GET_USER_DOCUMENTS] });
      // BE delete falls back to delete_job for processing/failed jobs (same id),
      // so optimistically remove from both lists.
      return optimisticRemove<DocumentsAndJobsResponse>(
        queryClient,
        [DOCUMENTS_AND_JOBS_KEY],
        (data) => ({
          ...data,
          documents: data.documents.filter((doc) => doc.document_id !== documentId),
          jobs: data.jobs.filter((job) => job.job_id !== documentId),
        })
      );
    },
    onSuccess: () => {
      toast({
        tone: 'success',
        title: t('userDocuments.deleteSuccess'),
        description: t('userDocuments.deleteSuccessDescription'),
      });
    },
    onError: (error, _documentId, context) => {
      rollbackOptimisticRemove(queryClient, context);

      trackChatError({
        errorType: 'document_delete',
        errorMessage: error.message || 'Failed to delete document',
        httpStatus: isAxiosError(error) ? error.response?.status : undefined,
      });
      toast({
        tone: 'destructive',
        title: t('userDocuments.deleteError'),
        description: error.message || t('userDocuments.deleteErrorDescription'),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [GET_USER_DOCUMENTS] });
      void queryClient.invalidateQueries({ queryKey: [DOCUMENTS_AND_JOBS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY] });
      void queryClient.invalidateQueries({
        queryKey: [COLLECTION_DETAIL_QUERY_KEY],
      });
    },
  });
}
